import React from 'react';
import { MapPin, Car, Home } from 'lucide-react';
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useScrollTrigger } from './hooks/useScrollTrigger';

const containerStyle = {
  width: "100%",
  height: "450px",
  borderRadius: "1rem", // rounded corners
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", // soft shadow
  border: "3px solid rgba(122, 62, 83, 0.15)", 
  marginTop: "2rem"
};

const location = {
  lat: -33.96907, 
  lng: 25.46977  
};

const mapOptions = {
  disableDefaultUI: false, // keep zoom and drag controls
  styles: [
    {
      elementType: "geometry",
      stylers: [{ color: "#f9f5f1" }], // soft beige background
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#7a3e53" }], // matches your heading color
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#f9f5f1" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "road",
      elementType: "geometry.stroke",
      stylers: [{ color: "#e6d3d8" }],
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [{ color: "#cde7df" }], // soft teal water
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [{ color: "#d6eadf" }], // light green parks
    },
  ],
};

const TravelSection = () => {
  const [headerRef, headerVisible] = useScrollTrigger();
  const [mapSectionRef, mapSectionVisible] = useScrollTrigger();
  const [hotelsRef, hotelsVisible] = useScrollTrigger();
  const [airbnbRef, airbnbVisible] = useScrollTrigger();

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div 
        ref={headerRef}
        className={`text-center mb-16 transition-all duration-1000 ${
          headerVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <Car className="mx-auto mb-6 text-[#9B4F96]" size={40} />
        <h2 className="text-4xl font-serif font-light text-[#7A3E53] mb-4">Travel & Accommodations</h2>
        <p className="text-[#5A8E6E] text-lg">Everything you need for your journey</p>
      </div>

      <div className="grid md:grid-cols-2 gap-10 mb-20">
        <div 
          ref={mapSectionRef}
          className={`p-10 border border-[#E76C9B]/20 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-500 ${
            mapSectionVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-8">Getting There</h3>
          <div className="space-y-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <MapPin size={24} className="text-[#E76C9B] animate-[bounce_2s_ease-in-out_infinite]" />
                <h4 className="font-serif text-[#7A3E53] text-lg">Venue Location</h4>
              </div>
              <p className="text-[#5A8E6E] pl-10">
                Swanlake Gardens<br />
                Lot 266 Kragga Kamma Rd<br />
                Theescombe, Gqeberha, 6070
              </p>
              <div 
                className={`hover:brightness-105 transition-all duration-500 ${
                  mapSectionVisible 
                    ? 'opacity-100' 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: mapSectionVisible ? '400ms' : '0ms' }}
              >
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={location}
                  zoom={15} // closer zoom to focus on location
                  options={mapOptions}
                >
                  {/* Marker pinned at the location */}
                  <Marker position={location} />
                </GoogleMap>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-10">
          <div 
            ref={hotelsRef}
            className={`p-10 border border-[#F36C3D]/20 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-500 ${
              hotelsVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-6">Recommended Hotels</h3>
            <div className="space-y-8">
              <div 
                className={`hover:text-[#7A3E53] transition-colors duration-300 ${
                  hotelsVisible 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 translate-x-4'
                }`}
                style={{ transitionDelay: hotelsVisible ? '200ms' : '0ms' }}
              >
                <h4 className="font-serif text-[#7A3E53] mb-2">The Hub Urban Hotel</h4>
                <p className="text-[#5A8E6E] text-sm mb-3">
                  217 Main Rd, Walmer, Gqeberha, 6070<br />
                  Phone: 041 581 2127
                </p>
              </div>

              <div 
                className={`hover:text-[#7A3E53] transition-colors duration-300 ${
                  hotelsVisible 
                    ? 'opacity-100 translate-x-0' 
                    : 'opacity-0 translate-x-4'
                }`}
                style={{ transitionDelay: hotelsVisible ? '400ms' : '0ms' }}
              >
                <h4 className="font-serif text-[#7A3E53] mb-2">Forest Hall Guest House</h4>
                <p className="text-[#5A8E6E] text-sm mb-3">
                  84 River Rd, Walmer, Gqeberha, 6070<br />
                  Phone: 072 020 9595
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div 
        ref={airbnbRef}
        className={`mb-20 transition-all duration-1000 ${
          airbnbVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="p-10 border border-[#F7C843]/30 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-500">
          <div className="flex items-center gap-4 mb-8">
            <Home size={32} className="text-[#F7C843]" />
            <h3 className="text-2xl font-serif font-light text-[#7A3E53]">Recommended Airbnbs</h3>
          </div>
          <p className="text-[#5A8E6E] mb-8">There are so many Airbnbs in the area! Here are a few we recommend:</p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Goodnight Accommodation Stepping Stone",
                url: "https://www.airbnb.co.za/rooms/1410826151427709637?adults=1&check_in=2026-02-20&check_out=2026-02-22&location=Gqeberha%2C%20South%20Africa&search_mode=regular_search&source_impression_id=p3_1757347626_P33s3MJmaqAHeTiC&previous_page_section_name=1001&federated_search_id=3570a03b-e050-4b8b-906f-330c84c84528",
                border: "border-[#F29AB3]/20"
              },
              {
                name: "Armadale Country Retreat",
                url: "https://www.airbnb.co.za/rooms/1383255307154808596?adults=1&check_in=2026-02-20&check_out=2026-02-22&location=Gqeberha%2C%20South%20Africa&search_mode=regular_search&source_impression_id=p3_1757347682_P3c5T0fwkc6ys5Sd&previous_page_section_name=1001&federated_search_id=3570a03b-e050-4b8b-906f-330c84c84528",
                border: "border-[#C38CC3]/20"
              },
              {
                name: "Tranquil Gem",
                url: "https://www.airbnb.co.za/rooms/774316737771718319?adults=1&check_in=2026-02-20&check_out=2026-02-22&location=Gqeberha%2C%20South%20Africa&search_mode=regular_search&source_impression_id=p3_1757347734_P3tfynT-t9cY60Nx&previous_page_section_name=1001&federated_search_id=3570a03b-e050-4b8b-906f-330c84c84528",
                border: "border-[#F68E56]/20"
              }
            ].map((airbnb, index) => (
              <div 
                key={index}
                className={`${airbnb.border} rounded-lg p-6 bg-white/60 hover:shadow-lg transition-all duration-300 ${
                  airbnbVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: airbnbVisible ? `${200 + index * 200}ms` : '0ms' }}
              >
                <h4 className="font-serif text-[#7A3E53] mb-3">{airbnb.name}</h4>
                <a
                  href={airbnb.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm bg-[#7A3E53] text-white py-2 px-4 rounded-full hover:bg-[#7A3E53]/90 hover:shadow-md transition-all duration-300 inline-block"
                >
                  View Listing
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelSection;